/**
 * Static configuration for the Admin Dashboard:
 *  - sidebar groups & items (ADMIN_NAV_GROUPS)
 *  - top menu labels per tab id (TOP_MENU_LABELS)
 *  - home quick-access tiles (HOME_TILES)
 *  - tile-usage tracking helpers
 *
 * Extracted from AdminDashboardPage.jsx (iter114 refactor) to keep that
 * orchestrator file under control. No logic changes — only relocation.
 */
import {
  LayoutGrid, Home, Building2, CalendarDays, Receipt, Megaphone,
  BookOpenCheck, LogIn, Users, Car, Star, MessageSquare, Inbox,
  FileEdit, BedDouble, Book, Activity, UserCog, ShieldCheck, Scale,
  ReceiptText, CalendarClock, FileText, Calculator, Sparkles, Download,
  Image as ImageIcon, CalendarRange, CreditCard, TrendingUp, History,
} from "lucide-react";

// ---------- Sidebar groups (macro-categorie) ----------
export const ADMIN_NAV_GROUPS = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    items: [
      { id: "home", label: "Dashboard", icon: LayoutGrid },
      { id: "group-home", label: "Homepage di Gruppo", icon: Building2 },
    ],
  },
  {
    id: "availability",
    label: "Disponibilità e Prezzi",
    icon: CalendarDays,
    items: [
      { id: "panoramica", label: "Panoramica PMS", icon: CalendarDays, path: "/admin/channel-manager" },
      { id: "disponibilita", label: "Disponibilità Camere", icon: CalendarDays, path: "/admin/calendar" },
      { id: "rate-plans", label: "Piani Tariffari", icon: Receipt },
      { id: "promos", label: "Promozioni", icon: Megaphone },
      { id: "rms", label: "Revenue Manager", icon: TrendingUp, notifKey: "rms" },
    ],
  },
  {
    id: "bookings",
    label: "Prenotazioni e Clienti",
    icon: BookOpenCheck,
    items: [
      { id: "ical-inbox", label: "iCal in arrivo", icon: Inbox, notifKey: "ical_inbox" },
      { id: "bookings", label: "Prenotazioni", icon: BookOpenCheck, notifKey: "bookings" },
      { id: "checkins", label: "Check-in", icon: LogIn, notifKey: "checkins" },
      { id: "calendar", label: "Calendario", icon: CalendarDays },
      { id: "customers", label: "Rubrica Clienti", icon: Users },
      { id: "shuttle", label: "Richieste Navetta", icon: Car, notifKey: "shuttle" },
      { id: "reviews", label: "Recensioni", icon: Star, notifKey: "reviews" },
      { id: "booking-history", label: "Storico & Ripristino", icon: History },
    ],
  },
  {
    id: "communication",
    label: "Comunicazione",
    icon: MessageSquare,
    items: [
      { id: "messages", label: "Messaggi", icon: Inbox, notifKey: "messages" },
      { id: "kb", label: "Q&A Concierge", icon: MessageSquare },
    ],
  },
  {
    id: "system",
    label: "Sistema",
    icon: UserCog,
    items: [
      { id: "content", label: "Contenuti sito", icon: FileEdit },
      { id: "rooms", label: "Camere", icon: BedDouble },
      { id: "photo-library", label: "Archivio Foto", icon: ImageIcon },
      { id: "guide-app", label: "App Guida Digitale", icon: Book, notifKey: "guide_alerts" },
      { id: "api-logs", label: "Storico API", icon: Activity },
      { id: "profile", label: "Profilo", icon: UserCog },
    ],
  },
  {
    id: "compliance",
    label: "Adempimenti e Report",
    icon: ShieldCheck,
    items: [
      { id: "compliance", label: "Adempimenti", icon: ShieldCheck, notifKey: "compliance" },
      { id: "cleaning-plan", label: "Pulizia da assegnare", icon: Sparkles },
      { id: "regulations", label: "Normativa & Regolamenti", icon: Scale, notifKey: "regulations" },
      { id: "receipts-quotes", label: "Ricevute & Preventivi", icon: ReceiptText },
      { id: "deadlines", label: "Scadenze", icon: CalendarClock, notifKey: "deadlines" },
      { id: "contracts", label: "Contratti", icon: FileText },
      { id: "management", label: "Contabilità", icon: Calculator },
      { id: "finance-import", label: "Import Fatture (AI)", icon: Sparkles },
      { id: "payment-methods", label: "Metodi di pagamento", icon: CreditCard },
      { id: "employees", label: "Dipendenti / Timbrature", icon: Users },
      { id: "export", label: "Export Dati", icon: Download },
    ],
  },
];

// ---------- Top menu labels (for "last visited" pin) ----------
export const TOP_MENU_LABELS = {
  "finance-import": "Contabilità",
  "payment-methods": "Pagamenti",
  "deadlines": "Scadenze",
  "rooms": "Camere",
  "rate-plans": "Piani Tariffari",
  "calendar": "Calendario",
  "checkins": "Check-in",
  "messages": "Messaggi",
  "kb": "Knowledge Base",
  "promos": "Offerte",
  "rms": "Revenue Manager",
  "guide": "Guida",
  "api-logs": "Storico API",
  "profile": "Profilo",
  "compliance": "Adempimenti",
  "regulations": "Normativa & Regolamenti",
  "receipts-quotes": "Ricevute & Preventivi",
  "management": "Guadagni",
  "employees": "Dipendenti",
  "export": "Export",
  "customers": "Rubrica",
  "shuttle": "Navette",
  "reviews": "Recensioni",
  "content": "Contenuti",
};

// ---------- Home quick-access tiles (max 12 to avoid overwhelming the dashboard) ----------
export const HOME_TILES = [
  { id: "bookings", label: "Prenotazioni", desc: "Gestisci richieste e ricevute", icon: BookOpenCheck, color: "from-[#1E3A5F] to-[#2A4F7C]" },
  { id: "customers", label: "Rubrica Clienti", desc: "Storico ospiti e habitué", icon: Users, color: "from-indigo-700 to-indigo-500" },
  { id: "shuttle", label: "Richieste Navetta", desc: "Approva / rifiuta con email auto", icon: Car, color: "from-cyan-700 to-cyan-500" },
  { id: "reviews", label: "Recensioni", desc: "Google · Booking · Airbnb", icon: Star, color: "from-amber-600 to-amber-400" },
  { id: "panoramica", label: "Panoramica PMS", desc: "Tutte le camere in un colpo d'occhio", icon: CalendarDays, color: "from-[#AC5D45] to-[#C9785C]", path: "/admin/channel-manager" },
  { id: "disponibilita", label: "Disponibilità & Prezzi", desc: "Calendario per camera", icon: CalendarRange, color: "from-emerald-700 to-emerald-500", path: "/admin/calendar" },
  { id: "calendar", label: "Calendario", desc: "Prenotazioni · disponibilità · eventi", icon: CalendarRange, color: "from-violet-700 to-violet-500" },
  { id: "messages", label: "Messaggi", desc: "Richieste flessibili e contatti", icon: Inbox, color: "from-sky-700 to-sky-500" },
  { id: "compliance", label: "Adempimenti", desc: "Polizia, ISTAT, City tax", icon: ShieldCheck, color: "from-rose-700 to-rose-500" },
  { id: "receipts-quotes", label: "Ricevute & Preventivi", desc: "Ricevute ospiti + preventivi PDF", icon: ReceiptText, color: "from-emerald-700 to-emerald-500" },
  { id: "content", label: "Contenuti sito", desc: "Modifica testi e foto", icon: FileEdit, color: "from-amber-700 to-amber-500" },
  { id: "management", label: "Contabilità", desc: "Bilancio, fornitori, magazzino", icon: Calculator, color: "from-slate-700 to-slate-500" },
];

// ---------- Tile usage tracking (sorted by recent usage) ----------
const LS_TILE_USAGE = "admin_tile_usage";

export const trackTileUse = (id) => {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_TILE_USAGE) || "{}");
    raw[id] = { count: (raw[id]?.count || 0) + 1, last: Date.now() };
    localStorage.setItem(LS_TILE_USAGE, JSON.stringify(raw));
  } catch (err) {
    console.warn("[tile-usage] localStorage write failed:", err);
  }
};

export const sortTilesByUsage = (tiles) => {
  let usage = {};
  try { usage = JSON.parse(localStorage.getItem(LS_TILE_USAGE) || "{}"); }
  catch (e) { console.warn("[AdminDashboard] tile usage parse failed", e); usage = {}; }
  return [...tiles].sort((a, b) => {
    const ua = usage[a.id] || { count: 0, last: 0 };
    const ub = usage[b.id] || { count: 0, last: 0 };
    if (ub.count !== ua.count) return ub.count - ua.count;
    return ub.last - ua.last;
  });
};

// ---------- Misc ----------
export const todayISO = () => new Date().toISOString().slice(0, 10);
